// Pre-Approval milestones — Section 2 (Conditional Approval Letters). Two
// attestation rows (Borrower + Seller) tracking Generated → Sent →
// ReceivedSigned. The letter documents are generated and signed in External
// Collection, whose approval chain also fills a blank milestone here; this
// page records the LA's attestation of progress and offers an optional
// Document Repository pointer for navigation.

import { useMemo } from "react";
import { Badge, Card } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import {
  useLinkLetterDocument,
  useMarkLetterGenerated,
  useMarkLetterReceivedSigned,
  useMarkLetterSent,
  usePreApprovalLetters,
  useUnlinkLetterDocument,
  useUpdateLetterNotes,
} from "@/hooks/deals/usePreApproval";
import {
  PREAPPROVAL_PERMISSIONS,
  type PreApprovalLetterDto,
  type PreApprovalLetterStatus,
  type PreApprovalPartyKind,
} from "@/types/deals";
import { PreApprovalAttestationRow, type MilestoneSpec } from "./PreApprovalAttestationRow";

interface Props {
  dealId: string;
}

const PARTY_ORDER: PreApprovalPartyKind[] = ["Borrower", "Seller"];

export function PreApprovalLettersSection({ dealId }: Props) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PREAPPROVAL_PERMISSIONS.IssueLetters);
  const query = usePreApprovalLetters(dealId);

  const markGenerated = useMarkLetterGenerated(dealId);
  const markSent = useMarkLetterSent(dealId);
  const markReceivedSigned = useMarkLetterReceivedSigned(dealId);
  const updateNotes = useUpdateLetterNotes(dealId);
  const unlinkDocument = useUnlinkLetterDocument(dealId);
  const _link = useLinkLetterDocument(dealId);
  void _link;

  // The two (Borrower + Seller) rows are self-healed on read by the API, so
  // they always exist for a deal in Pre-Approval or beyond.
  const rows = useMemo(() => {
    const byParty = new Map<PreApprovalPartyKind, PreApprovalLetterDto>();
    for (const r of query.data ?? []) byParty.set(r.party, r);
    return PARTY_ORDER.map((p) => byParty.get(p)).filter(
      (r): r is PreApprovalLetterDto => !!r,
    );
  }, [query.data]);

  const rollup = computeRollup(query.data ?? []);

  return (
    <Card>
      <div className="flex items-start justify-between flex-wrap gap-space-2 mb-space-3">
        <div>
          <div className="font-display font-bold text-h3 text-onyx">
            Section 2 — Conditional Approval Letters
          </div>
          <p className="text-small text-gray-mid m-0">
            Attest that the Borrower Conditional Loan Approval and the Seller
            Conditional Approval have each been Generated, Sent, and
            Received-Signed. A letter sent through its approval chain records
            these milestones as it moves; otherwise this page records your
            attestation.
          </p>
        </div>
        <Badge status={rollup.tone}>{rollup.label}</Badge>
      </div>

      <div className="flex flex-col gap-space-3">
        {rows.map((row) => {
          const milestones: MilestoneSpec[] = [
            {
              key: "generated",
              label: "Mark Generated",
              isComplete: statusGte(row.status, "Generated"),
              enabled: row.status === "NotStarted",
              at: row.generatedAt,
              by: row.generatedByDisplayName,
              onClick: () => markGenerated.mutateAsync(row.id),
              pending: markGenerated.isPending && markGenerated.variables === row.id,
            },
            {
              key: "sent",
              label: "Mark Sent",
              isComplete: statusGte(row.status, "Sent"),
              enabled: row.status === "Generated",
              at: row.sentAt,
              by: row.sentByDisplayName,
              onClick: () => markSent.mutateAsync(row.id),
              pending: markSent.isPending && markSent.variables === row.id,
            },
            {
              key: "received",
              label: "Mark Received-Signed",
              isComplete: statusGte(row.status, "ReceivedSigned"),
              enabled: row.status === "Sent",
              at: row.receivedSignedAt,
              by: row.receivedSignedByDisplayName,
              onClick: () => markReceivedSigned.mutateAsync(row.id),
              pending: markReceivedSigned.isPending && markReceivedSigned.variables === row.id,
            },
          ];

          return (
            <PreApprovalAttestationRow
              key={row.id}
              dealId={dealId}
              party={row.party}
              statusLabel={statusLabel(row.status)}
              statusTone={statusTone(row.status)}
              milestones={milestones}
              notes={row.notes}
              onSaveNotes={async (notes) => {
                await updateNotes.mutateAsync({ id: row.id, body: { notes } });
              }}
              notesPending={updateNotes.isPending && updateNotes.variables?.id === row.id}
              linkedDocumentId={row.linkedDocumentId}
              onUnlinkDocument={async () => {
                await unlinkDocument.mutateAsync(row.id);
              }}
              unlinkPending={unlinkDocument.isPending && unlinkDocument.variables === row.id}
              canEdit={canEdit}
            />
          );
        })}
      </div>
    </Card>
  );
}

function statusGte(s: PreApprovalLetterStatus, target: PreApprovalLetterStatus) {
  const order: PreApprovalLetterStatus[] = ["NotStarted", "Generated", "Sent", "ReceivedSigned"];
  return order.indexOf(s) >= order.indexOf(target);
}

function statusLabel(s: PreApprovalLetterStatus): string {
  switch (s) {
    case "NotStarted": return "Not started";
    case "Generated": return "Generated";
    case "Sent": return "Sent";
    case "ReceivedSigned": return "Received (signed)";
  }
}

function statusTone(s: PreApprovalLetterStatus): "draft" | "review" | "finalized" {
  switch (s) {
    case "NotStarted": return "draft";
    case "ReceivedSigned": return "finalized";
    default: return "review";
  }
}

function computeRollup(rows: PreApprovalLetterDto[]): { label: string; tone: "draft" | "review" | "finalized" } {
  if (rows.length === 0) return { label: "Not started", tone: "draft" };
  if (rows.every((r) => r.status === "ReceivedSigned")) return { label: "Complete", tone: "finalized" };
  if (rows.every((r) => r.status === "NotStarted")) return { label: "Not started", tone: "draft" };
  return { label: "In progress", tone: "review" };
}
